import { useEffect, useRef, useState } from "react";
import type {
  HostContext,
  ShellRuntime,
  ShellSessionSnapshot,
} from "@shared/host/shell-runtime";
import {
  buildAppliedHostContextSnapshot,
  computeHostContextChanges,
  computeIsInsideShell,
  type AppliedHostContextSnapshot,
} from "./host-bridge";
import {
  resolveHostBridgeContext,
  resolveHostBridgeSessionSnapshot,
} from "./host-bridge-hydration";

export interface HostBridgeHydrationInput {
  hostContext?: HostContext;
  providesSessionService: boolean;
  sessionSnapshot?: ShellSessionSnapshot;
}

export interface HostBridgeAuthAdapter {
  hydrate: (input: HostBridgeHydrationInput) => boolean;
  getSnapshot: () => {
    token: string | null;
    user: unknown | null;
  };
}

export interface HostBridgeTenantAdapter {
  setDomain: (domain: string) => void;
}

export interface HostBridgeObserver {
  onContextUpdated?: (context: HostContext) => void;
  onHydrated?: (snapshot: {
    token: string | null;
    user: unknown | null;
  }) => void;
  onMissingContext?: () => void;
  onReceivingContext?: (context: HostContext) => void;
}

export interface UseHostBridgeControllerOptions<TLocale> {
  auth: HostBridgeAuthAdapter;
  hostContext?: HostContext;
  observer?: HostBridgeObserver;
  readHostLocale: (context?: HostContext) => TLocale | undefined;
  readHostSnapshot: () => HostContext | undefined;
  readShellRuntime: () => ShellRuntime | undefined;
  redirectToShellLogin: () => void;
  isFederated: () => boolean;
  isStandalone: () => boolean;
  syncLocaleFromHost: (context: HostContext) => unknown | Promise<unknown>;
  tenant: HostBridgeTenantAdapter;
}

export interface HostBridgeControllerState<TLocale> {
  hostContext?: HostContext;
  hostLocale?: TLocale;
  isHydrated: boolean;
  isInsideShell: boolean;
}

export const useHostBridgeController = <TLocale>({
  auth,
  hostContext,
  observer,
  readHostLocale,
  readHostSnapshot,
  readShellRuntime,
  redirectToShellLogin,
  isFederated,
  isStandalone,
  syncLocaleFromHost,
  tenant,
}: UseHostBridgeControllerOptions<TLocale>): HostBridgeControllerState<TLocale> => {
  const shellRuntime = readShellRuntime();
  const hostStore = shellRuntime?.host;
  const sessionService = shellRuntime?.capabilities.session;
  const providesSessionService = Boolean(sessionService);
  const authRef = useRef(auth);
  const observerRef = useRef(observer);
  const readHostLocaleRef = useRef(readHostLocale);
  const readHostSnapshotRef = useRef(readHostSnapshot);
  const redirectToShellLoginRef = useRef(redirectToShellLogin);
  const syncLocaleFromHostRef = useRef(syncLocaleFromHost);
  const localeSyncQueueRef = useRef<Promise<void>>(Promise.resolve());
  const tenantRef = useRef(tenant);
  const isStandaloneRef = useRef(isStandalone);
  const isInsideShell = computeIsInsideShell({
    isFederated: isFederated(),
    hostContext,
    shellRuntime,
  });
  const [isHydrated, setIsHydrated] = useState(!isInsideShell);
  const [resolvedHostContext, setResolvedHostContext] = useState<
    HostContext | undefined
  >(() =>
    resolveHostBridgeContext({
      hostContext: hostContext ?? hostStore?.getSnapshot(),
      sessionSnapshot: sessionService?.getSnapshot(),
    }),
  );
  const [hostLocale, setHostLocale] = useState<TLocale | undefined>(() =>
    readHostLocale(hostContext ?? hostStore?.getSnapshot()),
  );
  const lastAppliedSnapshotRef = useRef<
    AppliedHostContextSnapshot<TLocale> | undefined
  >(undefined);

  authRef.current = auth;
  observerRef.current = observer;
  readHostLocaleRef.current = readHostLocale;
  readHostSnapshotRef.current = readHostSnapshot;
  redirectToShellLoginRef.current = redirectToShellLogin;
  syncLocaleFromHostRef.current = syncLocaleFromHost;
  tenantRef.current = tenant;
  isStandaloneRef.current = isStandalone;

  useEffect(() => {
    let hydrationTimeoutId: number | undefined;

    const applyHostContext = (nextContext?: HostContext) => {
      const sessionSnapshot = resolveHostBridgeSessionSnapshot({
        hostContext: nextContext,
        sessionSnapshot: sessionService?.getSnapshot(),
      });
      const exposedHostContext = resolveHostBridgeContext({
        hostContext: nextContext,
        sessionSnapshot,
      });
      const hydrationToken = sessionSnapshot?.token ?? null;
      if (!hydrationToken) {
        return false;
      }

      const nextLocale = readHostLocaleRef.current(exposedHostContext);
      const nextUserId = sessionSnapshot?.user?.id;
      const nextTenantSlug = sessionSnapshot?.tenant?.slug;
      const previousSnapshot = lastAppliedSnapshotRef.current;
      const {
        shouldHydrateSession,
        shouldSyncLocale,
        shouldSyncRouteBase,
      } = computeHostContextChanges({
        nextLocale,
        nextRouteBase: exposedHostContext?.routeBase,
        nextTenantSlug,
        nextSessionToken: hydrationToken ?? undefined,
        nextUserId,
        previousSnapshot,
      });

      if (shouldHydrateSession) {
        const didHydrate = authRef.current.hydrate({
          hostContext: nextContext,
          providesSessionService,
          sessionSnapshot,
        });
        if (!didHydrate) {
          return false;
        }

        if (nextTenantSlug) {
          tenantRef.current.setDomain(nextTenantSlug);
        }
      }

      if (shouldHydrateSession || shouldSyncLocale || shouldSyncRouteBase) {
        setResolvedHostContext(exposedHostContext);
      }

      if (nextLocale && previousSnapshot?.locale !== nextLocale) {
        setHostLocale(nextLocale);
      }

      if (shouldSyncLocale && nextContext) {
        localeSyncQueueRef.current = localeSyncQueueRef.current
          .then(() => syncLocaleFromHostRef.current(nextContext))
          .then(
            () => undefined,
            () => undefined,
          );
      }

      lastAppliedSnapshotRef.current = buildAppliedHostContextSnapshot({
        locale: nextLocale,
        routeBase: exposedHostContext?.routeBase,
        tenantSlug: nextTenantSlug,
        sessionToken: hydrationToken ?? undefined,
        userId: nextUserId,
      });

      if (hydrationTimeoutId !== undefined) {
        window.clearTimeout(hydrationTimeoutId);
      }

      hydrationTimeoutId = window.setTimeout(() => {
        const snapshot = authRef.current.getSnapshot();
        observerRef.current?.onHydrated?.(snapshot);
        setIsHydrated(true);
      }, 0);

      return true;
    };

    const readCurrentHostContext = () =>
      hostContext ?? hostStore?.getSnapshot() ?? readHostSnapshotRef.current();

    const runtimeContext = hostStore?.getSnapshot();
    const contextToUse = hostContext ?? runtimeContext;

    const applyCurrentContext = () => applyHostContext(readCurrentHostContext());

    if (contextToUse) {
      observerRef.current?.onReceivingContext?.(contextToUse);
      applyHostContext(contextToUse);
    }

    if (isStandaloneRef.current() && !contextToUse) {
      setIsHydrated(true);
      return () => {
        if (hydrationTimeoutId !== undefined) {
          window.clearTimeout(hydrationTimeoutId);
        }
      };
    }

    if (!contextToUse) {
      observerRef.current?.onMissingContext?.();

      const unsubscribeHost = hostStore?.subscribe(() => {
        const nextSnapshot = hostStore.getSnapshot();
        observerRef.current?.onContextUpdated?.(nextSnapshot);
        applyHostContext(nextSnapshot);
      });
      const unsubscribeSession = sessionService?.subscribe(() => {
        applyCurrentContext();
      });

      const timeoutId = window.setTimeout(() => {
        if (!applyCurrentContext()) {
          redirectToShellLoginRef.current();
        }
      }, 300);

      return () => {
        unsubscribeHost?.();
        unsubscribeSession?.();
        window.clearTimeout(timeoutId);
        if (hydrationTimeoutId !== undefined) {
          window.clearTimeout(hydrationTimeoutId);
        }
      };
    }

    const unsubscribeHost = hostStore?.subscribe(() => {
      const nextSnapshot = hostStore.getSnapshot();
      observerRef.current?.onContextUpdated?.(nextSnapshot);
      applyHostContext(nextSnapshot);
    });
    const unsubscribeSession = sessionService?.subscribe(() => {
      applyCurrentContext();
    });

    return () => {
      unsubscribeHost?.();
      unsubscribeSession?.();
      if (hydrationTimeoutId !== undefined) {
        window.clearTimeout(hydrationTimeoutId);
      }
    };
  }, [hostContext, hostStore, sessionService]);

  return {
    hostContext: resolvedHostContext,
    hostLocale,
    isHydrated,
    isInsideShell,
  };
};
